import React, { useRef, useState } from 'react'
import type { SupportedLanguage } from '../../../shared/i18n/types.no-deps'
import type { Profile } from '../user/Profile'
import { CommentEditor } from './CommentEditor'
import type { RichTextParagraph } from '../../../shared/domain/richText/types.no-deps'
import type { LexicalEditor } from 'lexical'
import { toSlate } from '../lexical/helpers/convert'
import { clear, getJson } from '../lexical/helpers/editor'
import { Spinner } from '../ui/Spinner'
import { isEmpty } from '../../../shared/domain/richText/helpers.no-deps'
import i18next from 'i18next'

export type Props = {
    profiles: Profile[]
    authorProfile?: Profile
    language: SupportedLanguage
    disabled: boolean
    topContent?: React.ReactElement
    onAction?: () => void
    onSubmit: (content: RichTextParagraph[]) => Promise<void>
    onTaggedUsersChange?: (userIds: number[]) => void
}

export function CommentAdder({
    profiles,
    language,
    disabled,
    authorProfile,
    topContent,
    onAction,
    onSubmit,
    onTaggedUsersChange,
}: Props) {
    const editorRef = useRef<LexicalEditor>(null)
    const [sending, setSending] = useState<boolean>(false)
    const onSubmitClick = async () => {
        const content = toSlate(getJson(editorRef.current))
        if (isEmpty(content)) {
            return
        }

        setSending(true)
        await onSubmit(content)
            .then(() => clear(editorRef.current))
            .catch()
        setSending(false)
    }

    return (
        <div className="AppCore__commentTimeline__CommentAdder">
            {topContent}
            <div className="AppCore__commentTimeline__CommentAdder__main">
                {authorProfile && (
                    <zaptic-profile-circle
                        name={authorProfile.name}
                        color={authorProfile.profileCircleColor}
                        profile-image-url={authorProfile.profileImageUrl}
                    />
                )}
                <div className="AppCore__commentTimeline__CommentAdder__Wrap">
                    <CommentEditor
                        ref={editorRef}
                        profiles={profiles}
                        language={language}
                        disabled={disabled || sending}
                        placeholder={i18next.t('appcore:TimelineComment.writeComment')}
                        onTaggedUsersChange={onTaggedUsersChange}
                    />
                    {onAction && (
                        <zaptic-button
                            size="auto"
                            type="transparent"
                            data-testid="comment-adder-create-action"
                            onClick={onAction}
                        >
                            <zaptic-icon
                                class="AppCore__commentTimeline__CommentAdder__action"
                                icon-name="plus-lightning"
                            />
                        </zaptic-button>
                    )}
                </div>
                {sending ? (
                    <div className="AppCore__commentTimeline__CommentAdder__spinner">
                        <Spinner size={39} />
                    </div>
                ) : (
                    <zaptic-button size="auto" type="transparent" onClick={onSubmitClick} test-id="comment-send">
                        <zaptic-icon class="AppCore__commentTimeline__CommentAdder__send" icon-name="send" />
                    </zaptic-button>
                )}
            </div>
        </div>
    )
}
